import { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { parse, stringify } from 'query-string';
import useAuth from '../../authentication/hooks/useAuth';
import { getJwtToken } from '../../authentication/utils/jwt';
import LoadingPage from '../../../pages/LoadingPage';
import RedirectErrorPage from './RedirectErrorPage';

const RedirectHandler = () => {
  const location = useLocation();
  const { changeBroker, isAuthorized, isChangingBroker } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [shouldSwitchBroker, setShouldSwitchBroker] = useState(false);

  useEffect(() => {
    const params = parse(location.search);
    const brokerId = params.broker_id as string | undefined;
    const redirectTo =
      (params.redirect_to as string | undefined) || '/dashboard';

    // Validate redirectTo is a valid path
    if (redirectTo && !redirectTo.startsWith('/')) {
      setError('Invalid redirect path. Path must start with "/".');
      return;
    }

    // If no broker_id provided, just redirect to redirectTo
    if (!brokerId) {
      setRedirectTo(redirectTo);
      return;
    }

    // If user is not authorized, redirect to login with return path
    if (!isAuthorized) {
      const returnPath = `/redirect?${stringify({
        broker_id: brokerId,
        redirect_to: redirectTo,
      })}`;
      setRedirectTo(`/login?return=${encodeURIComponent(returnPath)}`);
      return;
    }

    // Get current broker ID from JWT
    const jwtData = getJwtToken();
    const currentBrokerId = jwtData?.ext?.bid;

    // If broker IDs match, just redirect
    if (currentBrokerId === brokerId) {
      setRedirectTo(redirectTo);
      return;
    }

    // Need to switch broker
    setShouldSwitchBroker(true);
    changeBroker(
      { brokerId, redirectUri: redirectTo },
      {
        onError: (err: unknown) => {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to switch broker. Please try again.',
          );
          setShouldSwitchBroker(false);
        },
      },
    );
  }, [location.search, isAuthorized, changeBroker]);

  // Show loading during broker switch
  if (isChangingBroker || shouldSwitchBroker) {
    return <LoadingPage />;
  }

  // Show error page if there's an error
  if (error) {
    return <RedirectErrorPage errorMessage={error} />;
  }

  // Redirect if we have a target
  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  // Initial loading state
  return <LoadingPage />;
};

export default RedirectHandler;
